import { useEffect, useState } from "react";
import { Check, ChevronDown, ChevronUp, LoaderCircle } from "lucide-react";

const steps = [
  { label: "Parsing API", desc: "Analyzing endpoints & schemas" },
  { label: "Generating Tools", desc: "Creating MCP tool definitions" },
  { label: "Building Server", desc: "Packaging ready-to-deploy server" },
];

type ProcessingUIProps = {
  isUploading: boolean;
  isConverting: boolean;
};

export default function ProcessingUI({ isUploading, isConverting }: ProcessingUIProps) {
  const [collapsed, setCollapsed] = useState(false);
  const isRunning = isUploading || isConverting;

  useEffect(() => {
    if (isRunning) {
      setCollapsed(false);
      return;
    }
    setCollapsed(true);
  }, [isRunning]);

  if (!isRunning && collapsed) return null;

  const statuses: Array<"done" | "active" | "pending"> = isUploading
    ? ["active", "pending", "pending"]
    : isConverting
      ? ["done", "active", "active"]
      : ["done", "done", "done"];

  return (
    <section className="py-6 px-6">
      <div className="container max-w-2xl mx-auto rounded-xl border border-border bg-background/70 p-4">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="w-full flex items-center justify-between text-left"
        >
          <p className="text-sm font-semibold text-foreground">
            {isRunning ? "Processing your spec..." : "Processing complete"}
          </p>
          {collapsed ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          )}
        </button>

        {!collapsed && (
          <div className="mt-3 space-y-2">
            {steps.map((step, idx) => {
              const state = statuses[idx];
              return (
                <div key={step.label} className="flex items-start gap-2 text-sm">
                  {state === "done" ? (
                    <Check className="w-4 h-4 mt-0.5 text-green-600" />
                  ) : state === "active" ? (
                    <LoaderCircle className="w-4 h-4 mt-0.5 animate-spin text-foreground" />
                  ) : (
                    <span className="w-4 h-4 mt-0.5 rounded-full border border-muted-foreground/40" />
                  )}
                  <div>
                    <p className="text-foreground">{step.label}</p>
                    <p className="text-xs text-muted-foreground">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
